import { useState, useCallback } from 'react';
import { Form, message, Modal } from 'antd';
import { DataSourceType } from './useInitDataMap';
import { DataMapApi } from '@/api/dataMapApi';
import { DataSourceTableType } from '@/types/dataMap';

interface UseDataMapPanelProps {
  systems: DataSourceType[];
  setSystems: (systems: DataSourceType[]) => void;
  onSystemChange?: (systems: DataSourceType[]) => void;
  onTableDragStart: (datasourceId: string, tableId: string, e: React.DragEvent) => void;
  getDataSources: () => Promise<void>;
  mapViewsId: string | null;
}

export default function useDataMapPanel({
  systems,
  setSystems,
  onSystemChange,
  onTableDragStart,
  getDataSources,
  mapViewsId
}: UseDataMapPanelProps) {
  // 模态框状态
  const [addSystemModalVisible, setAddSystemModalVisible] = useState(false);

  // 添加表模态框状态
  const [addTableModalVisible, setAddTableModalVisible] = useState(false);
  
  // 上传文件模态框状态
  const [uploadFileModalVisible, setUploadFileModalVisible] = useState(false);
  // 当前编辑的系统ID
  const [currentSystemId, setCurrentSystemId] = useState<string | null>(null);
  // 当前选中的数据源ID
  const [selectedSystemId, setSelectedSystemId] = useState<string | null>(null);
  // 当前选中的表ID
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  // 是否正在编辑
  const [isEditing, setIsEditing] = useState(false);
  // 初始表单值
  const [initialValues, setInitialValues] = useState<any>(null);
  // 是否有修改
  const [hasChanges, setHasChanges] = useState(false);
  const [dataSourceTable, setDataSourceTable] = useState<DataSourceTableType[]>([]);
  // 数据源详情模态框可见性
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [filterSystemId, setFilterSystemId] = useState<string | null>(null);
  const [filterTableId, setFilterTableId] = useState<string | null>(null);
  const [form] = Form.useForm();

  // 筛选后的系统列表
  const filteredSystems: DataSourceType[] = (() => {
    const list = filterSystemId
      ? systems.filter(s => String(s.id) === String(filterSystemId))
      : systems;
    if (!filterTableId) return list;
    return list.map(s => ({
      ...s,
      tables: s.tables.filter(t => String(t.id) === String(filterTableId)),
    }));
  })();

  // 处理数据源点击
  const handleSystemClick = useCallback((systemId: string) => {
    setSelectedSystemId(systemId);
    const systemData = systems.find(s => String(s.id) === String(systemId));    
    if (systemData) {
      // 选择第一个表
      if(systemData.tables[0]?.id) setSelectedTableId(String(systemData.tables[0]?.id));
      // 初始化表单值
      const initialValues = {
        name: systemData.name,
        // 这里可以添加更多字段
        databaseType: systemData.databaseType,
        datasourceType: systemData.datasourceType,
        host: systemData.host,
        port: systemData.port,
        id: systemData.id,
        initDatabase: systemData.initDatabase,
        username: systemData.username,
        password: '', // 密码字段始终重置为空，避免不同数据源之间密码互相串扰
      };
      form.setFieldsValue(initialValues);
      setInitialValues(initialValues);
      setHasChanges(false);
      setDetailModalVisible(true);
    }
  }, [systems, form]);

  // 处理表点击
  const handleTableClick = useCallback((tableId: string) => {
    setSelectedTableId(tableId);
    if (selectedSystemId) {
      const system = systems.find(s => String(s.id) === String(selectedSystemId));
      const table = system?.tables.find(t => String(t.id) === String(tableId));
      if (system && table) {
        // 初始化表单值
        const initialValues = {
          name: system.name,
          // 这里可以添加更多字段
        };
        form.setFieldsValue(initialValues);
        setInitialValues(initialValues);
        setHasChanges(false);
        setIsEditing(true);
      }
    }
  }, [systems, selectedSystemId, form]);

  // 处理表单值变化
  const handleFormValuesChange = useCallback((changedValues: any, allValues: any) => {
    // 比较当前值和初始值，判断是否有修改
    const changed = JSON.stringify(allValues) !== JSON.stringify(initialValues);
    setHasChanges(changed);
  }, [initialValues]);

  // 处理保存
  const handleSave = useCallback(async () => {
    try {
      const validatedValues = await form.validateFields();
      // validateFields 只返回表单中注册了 Form.Item 的字段，这里需要拿到 id 等“隐藏字段”
      // 以便保存时能够更新已有数据源，而不是追加一条新数据
      const allValues = form.getFieldsValue(true);
      const formValues = { ...allValues, ...validatedValues };
      const savingId = String(formValues?.id ?? '');
      const existed = savingId
        ? systems.find(s => String(s.id) === savingId)
        : undefined;
      const datasourceType = (existed?.datasourceType ?? (formValues as any)?.datasourceType ?? 'RELATIONSHIP') as any;

      const res = await DataMapApi.updataRelateDataSource({
        ...formValues,
        datasourceType,
      });

      if (!!res && res.data && res.data.id) {
        const savedId = String(formValues?.id ?? res.data.id);
        const existedSaved = systems.find(s => String(s.id) === savedId);
        const newSystem = {
          ...res.data,
          tables: (res.data as any)?.tables ?? existedSaved?.tables ?? [],
        };

        const updatedSystems = existedSaved
          ? systems.map(s => (String(s.id) === savedId ? (newSystem as any) : s))
          : [...systems, (newSystem as any)];

        setSystems(updatedSystems);
        if (onSystemChange) onSystemChange(updatedSystems);
        
        // 保存成功后，更新初始值
        setInitialValues(formValues);
        setHasChanges(false);
        setDetailModalVisible(false);
        message.success('保存成功');
      } else {
        message.error('创建数据源失败');
        return;
      }
    } catch (error: any) {
      if (error.errorFields && error.errorFields.length > 0) {
        const firstError = error.errorFields[0];
        message.error(firstError.errors[0] || '表单验证失败，请检查输入');
      } else {
        message.error('表单验证失败，请检查输入');
      }
      console.error('表单验证错误:', error);
      return;
    }
  }, [form, systems, setSystems, onSystemChange]);

  // 处理取消
  const handleCancel = useCallback(() => {
    // 重置表单
    if (initialValues) {
      form.setFieldsValue(initialValues);
    }
    setHasChanges(false);
    setDetailModalVisible(false);
  }, [form, initialValues]);

  // 打开添加系统模态框
  const handleAddSystem = useCallback(() => {
    setCurrentSystemId('');
    setAddSystemModalVisible(true);
  }, []);

  // 关闭添加系统模态框
  const handleCloseAddSystemModal = useCallback(() => {
    setAddSystemModalVisible(false);
  }, []);

  // 处理系统添加
  const handleSystemAdded = useCallback((newSystem: DataSourceType) => {
    let updatedSystems;
    // 检查是否是本地节点数据源的更新
    if (newSystem.id === 'local-node') {
      // 更新本地节点数据源
      updatedSystems = systems.map(system => {
        if (String(system.id) === 'local-node') {
          return newSystem;
        }
        return system;
      });
    } else {
      // 添加新的数据源
      updatedSystems = [...systems, newSystem];
    }    
    setSystems(updatedSystems);
    if (onSystemChange) {
      onSystemChange(updatedSystems);
    }
  }, [systems, setSystems, onSystemChange]);

  // 删除数据源
  const handleDeleteSystem = useCallback((system: DataSourceType) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除数据源“${system.name}”吗？`,
      okText: '删除',
      cancelText: '取消',
      okType: 'danger',
      okButtonProps: {
        style: {
          backgroundColor: '#ff4d4f',
          color: '#fff',
        },
      },
      onOk: async () => {
        try {
          const res = await DataMapApi.deleteDataSource(system.id);
          if(res.code === 200) {
            const updatedSystems = systems.filter(data => String(data.id) !== String(system.id));
            setSystems(updatedSystems);
            if (onSystemChange) {
              onSystemChange(updatedSystems);
            }
            message.success(`删除数据源“${system.name}”成功`);
          }
        } catch (error: any) {
          message.error(error.message || '删除数据源失败，请重试');
        }
      },
    });
  }, [systems, setSystems, onSystemChange]);

  // 打开添加表模态框
  const handleAddTable = useCallback(async (systemId: string) => {
    try {
      const system = systems.find(s => String(s.id) === String(systemId));
      if (system?.datasourceType === 'FILES') {
        // 本地上传文件的数据源，打开上传文件模态框
        setCurrentSystemId(systemId);
        setUploadFileModalVisible(true);
      } else {
        // 其他类型的数据源，打开添加表模态框
        const res = await DataMapApi.getDataSourceMetadata(systemId);
        if(!!res.data?.length){
          setDataSourceTable([...res.data]);
        } else {
          setDataSourceTable([]);
        }
        setCurrentSystemId(systemId);
        setAddTableModalVisible(true);
      }
    } catch (error: any) {
      console.error('获取数据源元数据失败:', error);
      message.error('获取数据源元数据失败，请重试');
    }
  }, [systems]);

  // 关闭添加表模态框
  const handleCloseAddTableModal = useCallback(() => {
    setAddTableModalVisible(false);
    setCurrentSystemId(null);
  }, []);

  // 关闭上传文件模态框
  const handleCloseUploadFileModal = useCallback(() => {
    setUploadFileModalVisible(false);
    setCurrentSystemId(null);
  }, []);

  // 处理文件上传完成
  const handleFileUploaded = useCallback(async () => {
    // 重新获取数据源列表，更新表信息
    await getDataSources();
  }, [getDataSources]);

  // 处理表添加
  const handleTableAdded = useCallback(async (table: DataSourceTableType) => {
    if (!currentSystemId) return;
    try {
      console.log('currentSystemId', currentSystemId, table);
      const res = await DataMapApi.saveTableDataSource(currentSystemId, table.tableName);
      await getDataSources();
      console.log('currentSystemId2222', currentSystemId, table, res);
      if(res.code === 200){
        message.success(res.message || '保存数据表到数据源成功');
      }
    } catch (error: any) {
      console.error('保存数据表失败:', error);
      message.error(error.message || '保存数据表失败，请重试');
    }
  }, [currentSystemId, getDataSources]);

  // 删除表
  const handleDeleteTable = useCallback(async (systemId: string, tableId: string) => {
    try {
      if (!mapViewsId) {
        message.error('映射视图ID不存在，无法删除表');
        return;
      }
      
      // TODO 后台待出接口对接， 调用删除表的API
      const res = await DataMapApi.dataSourcesDeleteTable(
        systemId,
        tableId
      );
      
      console.log('删除表格:', res, tableId);
      if (res.code === 200) {
        // 更新systems状态，从对应的系统中移除该表
        const updatedSystems = systems.map(system => {
          if (String(system.id) === String(systemId)) {
            return {
              ...system,
              tables: system.tables.filter(table => String(table.id) !== String(tableId))
            };
          }
          return system;
        });
        
        setSystems(updatedSystems);
        if (onSystemChange) {
          onSystemChange(updatedSystems);
        }
        
        // 刷新数据源列表
        // await getDataSources();
        
        // 如果删除的是当前选中的表，清除选中状态
        if (String(selectedTableId) === String(tableId)) {
          setSelectedTableId(null);
        }
        
        message.success('删除表成功');
      } else {
        message.error(res.message || '删除表失败，请重试');
      }
    } catch (error: any) {
      console.error('删除表失败:', error);
      message.error(error?.message || '删除表失败，请重试');
    }
  }, [systems, setSystems, onSystemChange, selectedTableId, mapViewsId]);

  // 处理筛选系统变化
  const handleFilterSystemChange = useCallback((value: string | null) => {
    setFilterSystemId(value);
    setFilterTableId(null);
  }, []);

  // 处理筛选表变化
  const handleFilterTableChange = useCallback((value: string | null) => {
    setFilterTableId(value);
  }, []);

  return {
    addSystemModalVisible,
    addTableModalVisible,
    uploadFileModalVisible,
    currentSystemId,
    selectedSystemId,
    selectedTableId,
    detailModalVisible,
    hasChanges,
    dataSourceTable,
    filterSystemId,
    filterTableId,
    filteredSystems,
    form,
    handleSystemClick,
    handleTableClick,
    handleFormValuesChange,
    handleSave,
    handleCancel,
    handleAddSystem,
    handleCloseAddSystemModal,
    handleSystemAdded,
    handleDeleteSystem,
    handleAddTable,
    handleCloseAddTableModal,
    handleTableAdded,
    handleDeleteTable,
    handleCloseUploadFileModal,
    handleFileUploaded,
    handleFilterSystemChange,
    handleFilterTableChange
  };
}
